import { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  CheckCircle, XCircle, RefreshCw,
  Ban, Trash2, PauseCircle, Play, Wallet,
  Square, CheckSquare, AlertCircle, UserX, AlertTriangle
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useConfirm, usePrompt } from './CustomDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminUsers = ({ users, loadAdminData, searchQuery }) => {
  const { showConfirm, ConfirmDialog } = useConfirm();
  const { showPrompt, PromptDialog } = usePrompt();
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [inactiveUsers, setInactiveUsers] = useState([]);
  const [warnedUsers, setWarnedUsers] = useState([]);
  const [showInactivePanel, setShowInactivePanel] = useState(false);

  const toggleAdmin = async (userId, makeAdmin) => {
    const action = makeAdmin ? 'grant admin privileges to' : 'remove admin privileges from';
    const confirmed = await showConfirm(
      `Are you sure you want to ${action} this user? This action can be reversed.`,
      makeAdmin ? 'Grant Admin' : 'Remove Admin'
    );
    if (!confirmed) return;
    
    try {
      const endpoint = makeAdmin ? 'make-admin' : 'remove-admin';
      await axios.post(`${API}/admin/${endpoint}/${userId}`, {}, { withCredentials: true });
      toast.success(makeAdmin ? 'Admin added' : 'Admin removed');
      await loadAdminData();
    } catch (error) {
      console.error('Toggle admin error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update admin status');
    }
  };

  const banUser = async (userId) => {
    const confirmed = await showConfirm(
      'Are you sure you want to BAN this user? Their active matches will be cancelled.',
      'Ban User'
    );
    if (!confirmed) return;
    
    try {
      await axios.post(`${API}/admin/ban-user/${userId}`, {}, { withCredentials: true });
      toast.success('User banned');
      await loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to ban user');
    }
  };

  const unbanUser = async (userId) => {
    const confirmed = await showConfirm(
      'Are you sure you want to UNBAN this user?',
      'Unban User'
    );
    if (!confirmed) return;
    
    try {
      await axios.post(`${API}/admin/users/${userId}/unban`, {}, { withCredentials: true });
      toast.success('User unbanned');
      await loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unban user');
    }
  };

  const suspendUser = async (userId) => {
    const confirmed = await showConfirm(
      'Are you sure you want to SUSPEND this user?',
      'Suspend User'
    );
    if (!confirmed) return;
    
    try {
      await axios.post(`${API}/admin/users/${userId}/suspend`, {}, { withCredentials: true });
      toast.success('User suspended');
      await loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to suspend user');
    }
  };

  const unsuspendUser = async (userId) => {
    const confirmed = await showConfirm(
      'Are you sure you want to UNSUSPEND this user?',
      'Unsuspend User'
    );
    if (!confirmed) return;
    
    try {
      await axios.post(`${API}/admin/users/${userId}/unsuspend`, {}, { withCredentials: true });
      toast.success('User unsuspended');
      await loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unsuspend user');
    }
  };

  const resetWallet = async (userId) => {
    const confirmed = await showConfirm(
      'Are you sure you want to RESET this user\'s wallet?',
      'Reset Wallet'
    );
    if (!confirmed) return;
    
    const newBalance = await showPrompt('Enter new wallet balance:', 'Reset Wallet', '0');
    if (newBalance === null) return;
    
    try {
      const response = await axios.post(`${API}/admin/users/${userId}/reset-wallet?new_balance=${parseFloat(newBalance) || 0}`, {}, { withCredentials: true });
      toast.success(response.data.message || 'Wallet reset');
      await loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset wallet');
    }
  };

  const deleteUser = async (userId) => {
    const confirmed = await showConfirm(
      'Are you sure you want to PERMANENTLY DELETE this user? This action CANNOT be undone.',
      'Delete User Forever'
    );
    if (!confirmed) return;
    
    try {
      const response = await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      toast.success(response.data.message || 'User deleted');
      setSelectedUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
      await loadAdminData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const selectAllUsers = () => {
    const filteredUsers = users.filter(u => 
      (u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
       u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
       u.username?.toLowerCase().includes(searchQuery.toLowerCase()))
    );
    setSelectedUsers(new Set(filteredUsers.map(u => u.user_id)));
  };

  const deselectAllUsers = () => {
    setSelectedUsers(new Set());
  };

  const loadInactiveUsers = async () => {
    try {
      const [inactiveRes, warnedRes] = await Promise.all([
        axios.get(`${API}/admin/inactive-users`, { withCredentials: true }),
        axios.get(`${API}/admin/inactive-users/warned`, { withCredentials: true })
      ]);
      setInactiveUsers(inactiveRes.data.inactive_users || []);
      setWarnedUsers(warnedRes.data.users || []);
    } catch (error) {
      console.error('Failed to load inactive users:', error);
      toast.error('Failed to load inactive users');
    }
  };

  const warnSelectedInactiveUsers = async () => {
    const userIds = Array.from(selectedUsers).filter(id => 
      inactiveUsers.some(u => u.user_id === id)
    );
    
    if (userIds.length === 0) {
      toast.error('No inactive users selected');
      return;
    }
    
    const confirmed = await showConfirm(
      `Send 24-hour inactivity warning to ${userIds.length} user(s)?`,
      'Send Warnings'
    );
    if (!confirmed) return;
    
    try {
      const response = await axios.post(`${API}/admin/inactive-users/warn`, userIds, { withCredentials: true });
      toast.success(`Warned ${response.data.warned_count} user(s)`);
      setSelectedUsers(new Set());
      await loadInactiveUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send warnings');
    }
  };

  const deleteSelectedInactiveUsers = async () => {
    const userIds = Array.from(selectedUsers);
    
    if (userIds.length === 0) {
      toast.error('No users selected');
      return;
    }
    
    const confirmed = await showConfirm(
      `PERMANENTLY DELETE ${userIds.length} selected user(s)? This action CANNOT be undone.`,
      'Delete Users Forever'
    );
    if (!confirmed) return;
    
    try {
      const response = await axios.post(`${API}/admin/inactive-users/delete-bulk`, userIds, { withCredentials: true });
      toast.success(`Deleted ${response.data.deleted_count} user(s)`);
      if (response.data.errors) {
        response.data.errors.forEach(err => toast.error(err));
      }
      setSelectedUsers(new Set());
      await Promise.all([loadAdminData(), loadInactiveUsers()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete users');
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
      <ConfirmDialog />
      <PromptDialog />
      
      {/* User Management Toolbar */}
      <div className="glass-card rounded-xl p-4">
        <div className="flex flex-wrap gap-3 items-center justify-between">
          <div className="flex gap-2 items-center">
            <Button size="sm" variant="outline" className="btn-secondary" onClick={selectAllUsers}>
              <CheckSquare className="w-3 h-3 mr-1" /> Select All
            </Button>
            <Button size="sm" variant="outline" className="btn-secondary" onClick={deselectAllUsers}>
              <Square className="w-3 h-3 mr-1" /> Deselect All
            </Button>
            {selectedUsers.size > 0 && (
              <span className="text-white/50 text-sm">{selectedUsers.size} selected</span>
            )}
          </div>
          <div className="flex gap-2 items-center">
            <Button 
              size="sm" 
              variant="outline"
              className={showInactivePanel ? "btn-primary" : "btn-secondary"}
              onClick={() => {
                setShowInactivePanel(!showInactivePanel);
                if (!showInactivePanel) loadInactiveUsers();
              }}
            >
              <UserX className="w-3 h-3 mr-1" /> Inactive Users
            </Button>
            {selectedUsers.size > 0 && (
              <Button 
                size="sm" 
                variant="outline"
                className="text-[#FF2E2E] border-[#FF2E2E]/30 hover:bg-[#FF2E2E]/10"
                onClick={deleteSelectedInactiveUsers}
              >
                <Trash2 className="w-3 h-3 mr-1" /> Delete Selected ({selectedUsers.size})
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Inactive Users Panel */}
      {showInactivePanel && (
        <div className="glass-card rounded-xl p-4 border border-[#FFB800]/30">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-[#FFB800] font-medium flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Inactive Users (No deposits, &gt;7 days)
            </h3>
            <Button size="sm" variant="ghost" onClick={loadInactiveUsers}>
              <RefreshCw className="w-3 h-3" />
            </Button>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            {/* Users needing warning */}
            <div className="bg-white/5 rounded-lg p-3">
              <p className="text-sm text-white/70 mb-2">Need 24hr Warning ({inactiveUsers.length})</p>
              {inactiveUsers.length === 0 ? (
                <p className="text-white/40 text-xs">No users need warning</p>
              ) : (
                <>
                  <div className="max-h-40 overflow-y-auto space-y-1 mb-2">
                    {inactiveUsers.slice(0, 10).map(u => (
                      <div key={u.user_id} className="flex items-center gap-2 text-xs">
                        <button onClick={() => toggleUserSelection(u.user_id)} className="p-0.5">
                          {selectedUsers.has(u.user_id) ? 
                            <CheckSquare className="w-3 h-3 text-[#00F0FF]" /> : 
                            <Square className="w-3 h-3 text-white/30" />
                          }
                        </button>
                        <span className="text-white/70">{u.name}</span>
                        <span className="text-white/30 truncate">{u.email || u.username || 'No email'}</span>
                      </div>
                    ))}
                    {inactiveUsers.length > 10 && (
                      <p className="text-white/40 text-xs">+{inactiveUsers.length - 10} more</p>
                    )}
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="text-[#FFB800] border-[#FFB800]/30 w-full"
                    onClick={warnSelectedInactiveUsers}
                    disabled={!Array.from(selectedUsers).some(id => inactiveUsers.some(u => u.user_id === id))}
                  >
                    <AlertTriangle className="w-3 h-3 mr-1" /> Send 24hr Warning
                  </Button>
                </>
              )}
            </div>

            {/* Users eligible for deletion */}
            <div className="bg-white/5 rounded-lg p-3">
              <p className="text-sm text-white/70 mb-2">Warned &amp; Eligible for Deletion ({warnedUsers.length})</p>
              {warnedUsers.length === 0 ? (
                <p className="text-white/40 text-xs">No users ready for deletion</p>
              ) : (
                <>
                  <div className="max-h-40 overflow-y-auto space-y-1 mb-2">
                    {warnedUsers.slice(0, 10).map(u => (
                      <div key={u.user_id} className="flex items-center gap-2 text-xs">
                        <button onClick={() => toggleUserSelection(u.user_id)} className="p-0.5">
                          {selectedUsers.has(u.user_id) ? 
                            <CheckSquare className="w-3 h-3 text-[#00F0FF]" /> : 
                            <Square className="w-3 h-3 text-white/30" />
                          }
                        </button>
                        <span className="text-white/70">{u.name}</span>
                        <span className="text-[#FF2E2E] text-xs">24hr passed</span>
                      </div>
                    ))}
                    {warnedUsers.length > 10 && (
                      <p className="text-white/40 text-xs">+{warnedUsers.length - 10} more</p>
                    )}
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="text-[#FF2E2E] border-[#FF2E2E]/30 w-full"
                    onClick={deleteSelectedInactiveUsers}
                    disabled={!Array.from(selectedUsers).some(id => warnedUsers.some(u => u.user_id === id))}
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> Delete Inactive Users
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="glass-card rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5">
              <tr>
                <th className="text-left p-3 text-white/50 font-normal w-10">
                  <button onClick={selectedUsers.size === users.length ? deselectAllUsers : selectAllUsers}>
                    {selectedUsers.size === users.length && users.length > 0 ? 
                      <CheckSquare className="w-4 h-4 text-[#00F0FF]" /> : 
                      <Square className="w-4 h-4 text-white/30" />
                    }
                  </button>
                </th>
                <th className="text-left p-3 text-white/50 font-normal">User</th>
                <th className="text-left p-3 text-white/50 font-normal">Balance</th>
                <th className="text-left p-3 text-white/50 font-normal">Stats</th>
                <th className="text-left p-3 text-white/50 font-normal">Status</th>
                <th className="text-right p-3 text-white/50 font-normal">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr 
                  key={u.user_id} 
                  className={`border-t border-white/5 hover:bg-white/5 ${selectedUsers.has(u.user_id) ? 'bg-[#00F0FF]/5' : ''}`}
                  data-testid={`admin-user-${u.user_id}`}
                >
                  <td className="p-3">
                    <button onClick={() => toggleUserSelection(u.user_id)}>
                      {selectedUsers.has(u.user_id) ? 
                        <CheckSquare className="w-4 h-4 text-[#00F0FF]" /> : 
                        <Square className="w-4 h-4 text-white/30" />
                      }
                    </button>
                  </td>
                  <td className="p-3">
                    <p className="font-medium">{u.name}</p>
                    <p className="text-white/40 text-xs">{u.email || u.username}</p>
                  </td>
                  <td className="p-3 font-mono text-[#CCFF00]">${(u.cash_balance || u.balance || 0).toFixed(2)}</td>
                  <td className="p-3">
                    <span className="text-[#00FF94]">{u.wins || 0}W</span>
                    <span className="text-white/30"> / </span>
                    <span className="text-[#FF2E2E]">{u.losses || 0}L</span>
                    {u.elo_rating && <span className="text-white/40 ml-2">ELO: {u.elo_rating}</span>}
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {u.is_admin && <span className="text-xs px-2 py-0.5 bg-[#7000FF]/20 text-[#7000FF] rounded">Admin</span>}
                      {u.is_banned && <span className="text-xs px-2 py-0.5 bg-[#FF2E2E]/20 text-[#FF2E2E] rounded">Banned</span>}
                      {u.is_suspended && <span className="text-xs px-2 py-0.5 bg-[#FFB800]/20 text-[#FFB800] rounded">Suspended</span>}
                      {u.inactive_warning_sent && <span className="text-xs px-2 py-0.5 bg-[#FFB800]/20 text-[#FFB800] rounded">Warned</span>}
                    </div>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-1 flex-wrap">
                      <Button size="sm" variant="ghost" onClick={() => toggleAdmin(u.user_id, !u.is_admin)} 
                        className={u.is_admin ? "text-[#FF2E2E]" : "text-[#00FF94]"}
                        title={u.is_admin ? "Remove Admin" : "Make Admin"}>
                        {u.is_admin ? <XCircle className="w-3 h-3" /> : <CheckCircle className="w-3 h-3" />}
                      </Button>
                      {u.is_banned ? (
                        <Button size="sm" variant="ghost" className="text-[#00FF94]" onClick={() => unbanUser(u.user_id)} title="Unban">
                          <Play className="w-3 h-3" />
                        </Button>
                      ) : (
                        <Button size="sm" variant="ghost" className="text-[#FF2E2E]" onClick={() => banUser(u.user_id)} title="Ban">
                          <Ban className="w-3 h-3" />
                        </Button>
                      )}
                      {u.is_suspended ? (
                        <Button size="sm" variant="ghost" className="text-[#00FF94]" onClick={() => unsuspendUser(u.user_id)} title="Unsuspend">
                          <Play className="w-3 h-3" />
                        </Button>
                      ) : (
                        <Button size="sm" variant="ghost" className="text-[#FFB800]" onClick={() => suspendUser(u.user_id)} title="Suspend">
                          <PauseCircle className="w-3 h-3" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="text-white/50" onClick={() => resetWallet(u.user_id)} title="Reset Wallet">
                        <Wallet className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-[#FF2E2E]" onClick={() => deleteUser(u.user_id)} title="Delete User">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </motion.div>
  );
};

export default AdminUsers;
